import { useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { useApi } from '@fortem-monorepo/react-spa'
import { FormError } from '@fortem-monorepo/react-ui'
import { FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { BankLegsField } from '@/pages/instruments/new-instrument/form-fields/BankLegsField'
import { CounterpartyIdField } from '@/pages/instruments/new-instrument/form-fields/CounterpartyIdField'
import { CurrencyIdField } from '@/pages/instruments/new-instrument/form-fields/CurrencyIdField'
import { CurrencyIdInput } from '@/pages/instruments/new-instrument/form-fields/CurrencyIdInput'
import { EffectiveDateFormField } from '@/pages/instruments/new-instrument/form-fields/EffectiveDateFormField'
import { FundIdField } from '@/pages/instruments/new-instrument/form-fields/FundIdField'
import { InstrumentIdByTypeInput } from '@/pages/instruments/new-instrument/form-fields/InstrumentIdByTypeInput'
import { MaturityDateField } from '@/pages/instruments/new-instrument/form-fields/MaturityDateField'
import { QuoteFactorFormField } from '@/pages/instruments/new-instrument/form-fields/QuoteFactorFormField'
import { StructureNameField } from '@/pages/instruments/new-instrument/form-fields/StructureNameField'
import { UnitMultiplierFormField } from '@/pages/instruments/new-instrument/form-fields/UnitMultiplierFormField'
import { AswapSchemaType } from '@/pages/instruments/new-instrument/schemas/aswap.schema'

export interface AswapFormFieldsProps {}

export const AswapFormFields: React.FC<AswapFormFieldsProps> = ({}) => {
  const { control, trigger, setValue, watch } = useFormContext<AswapSchemaType>()

  const bond_instrument_id = watch('bond_instrument_id')

  const api = useApi()

  const bondInstrumentQuery = useQuery({
    queryKey: ['instrument', { instrument_id: bond_instrument_id }],
    queryFn: () => api.instruments.getInstrumentById(bond_instrument_id),
    enabled: typeof bond_instrument_id === 'number',
  })

  useEffect(() => {
    const bond_currency_id = bondInstrumentQuery.data?.currency_id
    if (bond_currency_id) {
      setValue('bond_currency_id', bond_currency_id, {
        shouldValidate: true,
      })
    }
  }, [bondInstrumentQuery.data])

  useEffect(() => {
    trigger()
  }, [])

  return (
    <div className={'flex flex-col gap-4'}>
      <FormFieldRow>
        <StructureNameField placeholder={'ASWAP31'} />
        <FundIdField />
        <CurrencyIdField />
      </FormFieldRow>
      <FormFieldRow>
        <CounterpartyIdField />
        <EffectiveDateFormField />
        <MaturityDateField />
      </FormFieldRow>
      <FormFieldRow>
        <FormField
          name="bond_instrument_id"
          control={control}
          render={({ field }) => {
            return (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Bond</FormLabel>
                  <FormError />
                </div>
                <InstrumentIdByTypeInput
                  instrument_type_id={2}
                  className={'min-w-auto'}
                  placeholder={'Select a bond...'}
                  value={field.value?.toString()}
                  setValue={(value) => {
                    setValue('bond_instrument_id', parseInt(value), { shouldValidate: true })
                  }}
                />
              </FormItem>
            )
          }}
        />
        <FormField
          name="bond_currency_id"
          control={control}
          render={({ field }) => {
            return (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Bond Currency</FormLabel>
                  <FormError />
                </div>
                <CurrencyIdInput
                  disabled={true}
                  loading={bondInstrumentQuery.isFetching}
                  className={'min-w-auto'}
                  placeholder={"Select the bond's currency..."}
                  value={field.value?.toString()}
                />
              </FormItem>
            )
          }}
        />
      </FormFieldRow>
      <FormFieldRow>
        <UnitMultiplierFormField />
        <QuoteFactorFormField />
      </FormFieldRow>
      <BankLegsField />
    </div>
  )
}
